import { Component, inject, signal } from '@angular/core';
import { take } from 'rxjs';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwButton, UwButtonVariant } from '../../ui/button/uw-button';
import { UwSkeleton } from '../../ui/skeleton/uw-skeleton';
import { UwEmptyState } from '../../ui/empty-state/uw-empty-state';
import { CopilotService } from '../../../core/services/copilot.service';
import { CopilotStore } from '../../../core/stores/copilot.store';
import { DialogService } from '../../../core/services/dialog.service';
import { ToastService } from '../../../core/services/toast.service';
import { CommandService } from '../../../core/services/command.service';
import type { AiAction } from '../../../core/models/ai.model';

@Component({
  selector: 'uw-ai-action-hub',
  standalone: true,
  imports: [UwIcon, UwButton, UwSkeleton, UwEmptyState],
  template: `
    <div class="flex h-full flex-col">
      @if (copilot.actionBanner(); as banner) {
        <div class="mb-3 flex items-center gap-2.5 rounded-lg border border-success/30 bg-success-soft px-3 py-2.5">
          <uw-icon name="check-circle" [size]="16" class="shrink-0 text-success" />
          <p class="flex-1 text-[12px] font-medium text-success">{{ banner }}</p>
          <button
            class="rounded p-0.5 text-success/70 transition-colors hover:text-success"
            (click)="copilot.setActionBanner(null)"
            aria-label="Dismiss banner"
          >
            <uw-icon name="x" [size]="14" />
          </button>
        </div>
      }

      <div class="mb-3 flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <uw-icon name="zap" [size]="15" class="text-ai" />
          <h2 class="typo-title text-primary">Action Hub</h2>
        </div>
        <span class="rounded-full bg-ai-soft px-2 py-0.5 text-[10px] font-bold text-ai">Copilot</span>
      </div>

      <div class="flex-1 space-y-2 overflow-y-auto pr-1">
        @if (loading()) {
          @for (i of [0, 1, 2, 3]; track i) {
            <div class="space-y-2 rounded-lg border border-line bg-surface-elevated p-3">
              <uw-skeleton width="40%" height="13px" />
              <uw-skeleton width="75%" height="11px" />
            </div>
          }
        } @else if (actions().length === 0) {
          <uw-empty-state title="No suggested actions" message="Copilot suggestions will appear here as the workload changes." icon="zap" />
        } @else {
          @for (action of actions(); track action.id) {
            <div class="flex items-center gap-3 rounded-lg border border-line bg-surface-elevated p-3 transition-colors" [class.border-success/40]="isDone(action.id)">
              <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" [class]="iconTone(action)">
                <uw-icon [name]="action.icon" [size]="15" />
              </span>
              <div class="min-w-0 flex-1">
                <p class="truncate text-[12.5px] font-semibold text-primary">
                  {{ action.label }}
                  @if (action.count) {
                    <span class="ml-1 rounded-full bg-ai-soft px-1.5 py-px text-[10px] font-bold text-ai">{{ action.count }}</span>
                  }
                </p>
                @if (action.description) {
                  <p class="mt-0.5 truncate text-[11px] text-text-muted">{{ action.description }}</p>
                }
              </div>
              <uw-button
                [variant]="buttonVariant(action)"
                size="sm"
                [loading]="running() === action.id"
                [disabled]="isDone(action.id)"
                (click)="run(action)"
              >
                @if (isDone(action.id)) {
                  <span class="flex items-center gap-1"><uw-icon name="check" [size]="12" /> Done</span>
                } @else {
                  {{ action.type === 'run-risk-analysis' ? 'Run' : 'Execute' }}
                }
              </uw-button>
            </div>
          }
        }
      </div>

      <p class="mt-3 flex items-start gap-1.5 rounded-lg bg-surface-sunken/70 px-3 py-2 text-[10.5px] leading-snug text-text-muted">
        <uw-icon name="shield-check" [size]="12" class="mt-0.5 shrink-0" />
        All actions are logged to the audit trail with provenance. You approve every step — the AI never takes final action autonomously.
      </p>
    </div>
  `,
})
export class UwAiActionHub {
  private readonly copilotService = inject(CopilotService);
  protected readonly copilot = inject(CopilotStore);
  private readonly dialog = inject(DialogService);
  private readonly toast = inject(ToastService);
  private readonly command = inject(CommandService);

  protected readonly actions = signal<AiAction[]>([]);
  protected readonly loading = signal(true);
  protected readonly running = signal<string | null>(null);
  protected readonly done = signal<Set<string>>(new Set());

  constructor() {
    this.copilotService.getActions().pipe(take(1)).subscribe((list) => {
      this.actions.set(list);
      this.loading.set(false);
    });
  }

  protected isDone(id: string): boolean {
    return this.done().has(id);
  }

  protected iconTone(action: AiAction): string {
    const tones: Record<string, string> = {
      primary: 'bg-ai-soft text-ai',
      secondary: 'bg-primary-soft text-primary',
      danger: 'bg-danger-soft text-danger',
    };
    return tones[action.severity] ?? 'bg-primary-soft text-primary';
  }

  protected buttonVariant(action: AiAction): UwButtonVariant {
    if (action.severity === 'danger') return 'danger-outline';
    if (action.severity === 'primary') return 'ai';
    return 'secondary';
  }

  protected run(action: AiAction): void {
    if (this.running()) return;
    switch (action.type) {
      case 'bulk-quote':
        this.dialog.confirm({
          title: action.label,
          message: action.description ?? '',
          tone: 'success',
          confirmLabel: 'Issue quotes',
          details: ['5 straight-through renewals will be sent to their brokers.', 'Each quote is logged to the audit trail with full provenance.'],
          onConfirm: () => this.execute(action, 'Quotes issued to brokers'),
        });
        break;
      case 'review-chasers':
        this.dialog.confirm({
          title: action.label,
          message: action.description ?? '',
          confirmLabel: 'Send chasers',
          details: ['Chasers for Nolan Logistics (SOV) and Coastal Warehousing (loss runs) are ready.', 'Brokers will receive an automated follow-up with the missing items.'],
          onConfirm: () => this.execute(action, 'Chasers sent to 2 brokers'),
        });
        break;
      case 'create-referral':
        this.dialog.confirm({
          title: action.label,
          message: action.description ?? '',
          tone: 'warning',
          confirmLabel: 'Create & route',
          details: ['TIV $18.4M exceeds your $15M authority.', 'Referral memo is drafted and routed to David Park (UW Manager).'],
          onConfirm: () => this.execute(action, 'Referral routed to David Park'),
        });
        break;
      case 'escalate':
        this.dialog.confirm({
          title: action.label,
          message: action.description ?? '',
          tone: 'danger',
          confirmLabel: 'Escalate',
          details: ['The account will be flagged for immediate UW Management attention.'],
          onConfirm: () => this.execute(action, 'Account escalated to UW Management'),
        });
        break;
      case 'request-docs':
        this.dialog.confirm({
          title: action.label,
          message: action.description ?? '',
          confirmLabel: 'Request documents',
          onConfirm: () => this.execute(action, 'Document requests sent to brokers'),
        });
        break;
      case 'run-risk-analysis':
        this.execute(action, 'Risk Analysis re-run complete');
        break;
      default:
        this.execute(action, `${action.label} completed`);
    }
  }

  private execute(action: AiAction, banner: string): void {
    this.running.set(action.id);
    setTimeout(() => {
      this.running.set(null);
      this.done.update((set) => new Set(set).add(action.id));
      this.copilot.setActionBanner(banner);
      this.command.closePalette();
      const msg =
        action.type === 'escalate'
          ? 'The account was escalated to UW Management.'
          : action.type === 'run-risk-analysis'
            ? 'The Risk Assessment Engine finished scoring the active account.'
            : banner;
      const type = action.type === 'escalate' ? ('warning' as const) : ('success' as const);
      this.toast[type](action.label, msg);
    }, action.type === 'run-risk-analysis' ? 1600 : 700);
  }
}
